import React, { useState } from 'react';
import { Table, Button, Tag, notification } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmationModal from '../molecules/modals/ConfirmationModal';
import { useListConnectedAppsQuery, useRevokeConnectedAppMutation } from '../../../services/oauthApi';

const formatDate = (value) => (value ? new Date(value).toLocaleString() : null);
const formatDay = (value) => (value ? new Date(value).toLocaleDateString() : null);

// "Connected apps": AI apps the user connected to Workflo via OAuth ("Connect"
// in Claude / ChatGPT / Claude Code). Disconnecting revokes the connection and
// every token it holds immediately.
// compact: two columns (everything about an app in one cell, then Disconnect)
// and tighter padding, for the Settings modal's Integrations tab.
const ConnectedAppsSettings = ({ compact = false }) => {
  const intl = useIntl();
  const { data, isFetching } = useListConnectedAppsQuery();
  const [revokeApp, { isLoading: isRevoking }] = useRevokeConnectedAppMutation();
  const [revokeTarget, setRevokeTarget] = useState(null);
  const apps = data?.data || [];
  const pad = compact ? 'px-4 py-3' : 'px-6 py-4';
  const neverText = intl.formatMessage({ id: 'PAT_NEVER_LOWER', defaultMessage: 'never' });
  // "connected 10/01/2026 · last used 10/06/2026" under the app name.
  const metaLine = (record) =>
    [
      intl.formatMessage(
        { id: 'CONNECTED_APP_CONNECTED_ON', defaultMessage: 'connected {date}' },
        { date: formatDay(record.activatedAt) || '—' }
      ),
      intl.formatMessage(
        { id: 'PAT_LAST_USED_ON', defaultMessage: 'last used {date}' },
        { date: formatDay(record.lastUsedAt) || neverText }
      ),
    ].join(' · ');

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    try {
      await revokeApp(revokeTarget._id).unwrap();
    } catch (err) {
      notification.error({
        message: intl.formatMessage({ id: 'CONNECTED_APP_REVOKE_FAILED', defaultMessage: 'Failed to disconnect the app' }),
        placement: 'bottomRight',
      });
    } finally {
      setRevokeTarget(null);
    }
  };

  const allColumns = [
    {
      title: <FormattedMessage id="CONNECTED_APP_NAME" defaultMessage="App" />,
      key: 'app',
      render: (_, record) => (
        <div className="flex flex-col">
          <span className="text-sm text-gray-900">{record.clientDisplayName}</span>
          {record.redirectHost && <span className="text-xs text-gray-500">{record.redirectHost}</span>}
          {compact && (
            <>
              <div className="mt-1">
                {(record.scopes || []).map((scope) => <Tag key={scope} className="mb-0.5">{scope}</Tag>)}
              </div>
              <span className="text-xs text-gray-500 mt-0.5">{metaLine(record)}</span>
            </>
          )}
        </div>
      ),
    },
    {
      title: <FormattedMessage id="PAT_SCOPES" defaultMessage="Scopes" />,
      dataIndex: 'scopes',
      key: 'scopes',
      render: (scopes) => (scopes || []).map((scope) => <Tag key={scope}>{scope}</Tag>),
    },
    {
      title: <FormattedMessage id="CONNECTED_APP_CONNECTED" defaultMessage="Connected" />,
      dataIndex: 'activatedAt',
      key: 'activatedAt',
      render: (value) => formatDate(value) || '—',
    },
    {
      title: <FormattedMessage id="PAT_LAST_USED" defaultMessage="Last used" />,
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (value) => formatDate(value) || <FormattedMessage id="PAT_NEVER" defaultMessage="Never" />,
    },
    {
      title: <FormattedMessage id="PAT_ACTIONS" defaultMessage="Actions" />,
      key: 'actions',
      render: (_, record) => (
        <Button danger size="small" onClick={() => setRevokeTarget(record)}>
          <FormattedMessage id="CONNECTED_APP_DISCONNECT" defaultMessage="Disconnect" />
        </Button>
      ),
    },
  ];
  const columns = compact
    ? allColumns
        .filter((c) => ['app', 'actions'].includes(c.key))
        .map((c) => (c.key === 'actions' ? { ...c, title: null, align: 'right', width: 110 } : c))
    : allColumns;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className={`${pad} border-b border-gray-100`}>
        <h2 className="text-sm font-semibold text-gray-900">
          <FormattedMessage id="CONNECTED_APPS_TITLE" defaultMessage="Connected apps" />
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          <FormattedMessage
            id="CONNECTED_APPS_DESCRIPTION"
            defaultMessage="AI apps you connected to Workflo (for example Claude or ChatGPT). They act as you with the scopes shown."
          />
        </p>
      </div>
      <div className={pad}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={apps}
          loading={isFetching}
          pagination={false}
          size="small"
          locale={
            compact
              ? { emptyText: <span className="text-xs text-gray-400"><FormattedMessage id="CONNECTED_APPS_EMPTY" defaultMessage="No apps connected yet." /></span> }
              : undefined
          }
        />
      </div>
      <ConfirmationModal
        isModalOpen={!!revokeTarget}
        title={<FormattedMessage id="CONNECTED_APP_REVOKE_TITLE" defaultMessage="Disconnect app?" />}
        description={
          <FormattedMessage
            id="CONNECTED_APP_REVOKE_DESCRIPTION"
            defaultMessage="{app} will immediately lose access to your Workflo account. You can connect it again later."
            values={{ app: revokeTarget?.clientDisplayName }}
          />
        }
        onYesClick={handleRevokeConfirm}
        onCancel={() => setRevokeTarget(null)}
        yesText={isRevoking ? <FormattedMessage id="CONNECTED_APP_REVOKING" defaultMessage="Disconnecting..." /> : undefined}
        danger
      />
    </div>
  );
};

export default ConnectedAppsSettings;
